import {
    ChangeDetectorRef,
    Component,
    DestroyRef,
    Inject,
    Input,
    Optional,
    PLATFORM_ID,
    inject,
    signal,
} from "@angular/core";
import { Partner } from "@app/models/partner/IPartner";
import { AuthGuardService } from "@app/services/auth-guard.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { Customer } from "@app/models/customers/Customer";
import { MEDIA_TOKEN } from "tockens";
import { PartnerloginComponent } from "@app/components/modals/partnerlogin/partnerlogin.component";
import { AuthService } from "@app/services/auth.service";
import { OfferpackService } from "@app/services/offerpack.service";
import { ActivatedRoute, Router } from "@angular/router";
import { ChatService } from "@app/services/chat.service";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { FavoritesService } from "@app/services/favorites.service";
import { Item } from "@app/models/hotels/Item";
import { Price } from "@app/models/hotels/Price";
import { Menu } from "../Menu";
import { environment } from "src/environments/environment";
import { I18nService } from "@app/services/i18n.service";
import { MenuService } from "@app/services/menu.service";

export interface PathHistory {
    country: Item;
    region: Item;
    hotel: Item;
    flug: string;
    booking: Price;
}

@Component({
    selector: "clever-header2",
    templateUrl: "./header2.component.html",
    styleUrls: ["./header2.component.scss"],
    standalone: false,
})
export class HeaderDefault2Component {
    partner: Partner;

    deskmode = signal("");
    menuactive: boolean = false;
    isBrowser = signal(false);
    fixsetoultip: boolean = false;
    showsearch: boolean = false;
    @Input() isBooking: boolean = false;

    tooltiptext: string = "";
    logo = signal<string>("");
    partnername: string = "";
    destroyRef = inject(DestroyRef);

    currentCustomer: Customer;
    pathHistory: PathHistory;

    desktopTemplate: boolean = true;
    mobile: boolean = false;

    favoritesCount: number = 0;
    offerpackCount: number = 0;

    constructor(
        protected menu: Menu,
        protected dialog: MatDialog,
        protected authService: AuthService,
        protected router: Router,
        protected favoritesService: FavoritesService,
        protected i18nService: I18nService,
        protected cdr: ChangeDetectorRef,
        protected chatService: ChatService,
        protected route: ActivatedRoute,
        protected guardService: AuthGuardService,
        protected offerpackService: OfferpackService,
        protected menuService: MenuService,
        @Inject(PLATFORM_ID) protected platformId: any,
        @Inject(DOCUMENT) private document: any,
        protected mediaObserver: BreakpointObserver,
        @Optional() @Inject(MEDIA_TOKEN) private media_token: string
    ) {
        this.isBrowser.set(isPlatformBrowser(this.platformId));
        this.desktopTemplate = this.media_token !== "xs";
        this.mobile = this.media_token === "xs";
        this.deskmode.set(this.media_token);
    }

    ngOnInit(): void {
        this.initPartner();
        this.initMedia();
        this.initFavoriten();
        this.initOfferpack();
    }

    async initOfferpack() {
        this.offerpackService.offerpack$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((count: number) => {
                this.offerpackCount = count;
                this.cdr.detectChanges();
            });
    }

    async initFavoriten() {
        this.favoritesService.favorites$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((count: number) => {
                this.favoritesCount = count;
                this.cdr.detectChanges();
            });

        this.menuService.menu
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((obj) => {
                if (obj != this.menuactive) {
                    if (this.menuactive) {
                        this.closeMenu(null);
                    } else {
                        this.openMenu(null);
                    }
                }
            });
        this.menuService.search
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((obj) => {
                this.showsearch = obj;
            });
    }

    changeLng(lang) {
        window.localStorage.setItem("current_language", lang);
        this.i18nService.setLanguage(lang);
    }

    async initMedia() {
        if (this.mediaObserver.isMatched(Breakpoints.XSmall)) {
            this.desktopTemplate = false;
            this.mobile = true;
            this.deskmode.set("xs");
        }
        if (this.mediaObserver.isMatched(Breakpoints.Small)) {
            this.desktopTemplate = false;
            this.mobile = true;
            this.deskmode.set("sm");
        }
        if (this.mediaObserver.isMatched(Breakpoints.Medium)) {
            this.desktopTemplate = true;
            this.mobile = false;
            this.deskmode.set("md");
        }
        if (this.mediaObserver.isMatched(Breakpoints.Large)) {
            this.desktopTemplate = true;
            this.mobile = false;
            this.deskmode.set("lg");
        }
        if (this.mediaObserver.isMatched(Breakpoints.XLarge)) {
            this.desktopTemplate = true;
            this.mobile = false;
            this.deskmode.set("xl");
        }
        this.cdr.detectChanges();
    }

    async initPartner() {
        this.guardService.getPartner$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((obj) => {
                if (obj?.partnerid?.length > 0) {
                    this.partner = obj;
                    this.tooltiptext =
                        this.partner?.tel +
                        "\nBeratung & Buchung täglich von \n\n" +
                        (this.partner?.opening_period
                            ? this.partner.opening_period
                            : "08 - 23 Uhr");
                    if (this.partner?.logo?.length > 0 && environment.production) {
                        const dmn =
                            this.document.location.hostname === "localhost"
                                ? "localhost:4200"
                                : this.document.location.hostname;
                        this.logo.set(
                            "https://" +
                            dmn +
                            "/logo/" +
                            this.partner.partnerid +
                            "/" +
                            this.partner.logo
                        );
                    }

                    if (obj?.firstname?.length > 0) {
                        this.partnername = obj.firstname + " " + obj.lastname;
                    }
                    this.cdr.detectChanges();
                    this.initData();
                } else {
                    this.partner = null;
                    this.partnername = "";
                }
            });
    }

    async initData() {
        this.authService.auth$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((customer: Customer) => {
                this.currentCustomer = customer;
                this.cdr.detectChanges();
            });
        this.pathHistory = {} as PathHistory;
    }

    closeMenu(ev) {
        ev?.stopPropagation();
        document.body.style.overflow = "";
        this.menuactive = false;
        this.menuService.menu = this.menuactive;
    }

    openMenu(ev) {
        ev?.stopPropagation();
        this.menu.resetHistory2();
        document.body.style.overflow = "hidden";
        this.menuactive = true;
        this.menuService.menu = this.menuactive;
    }

    async gotoPartner() {
        let target = {} as MatDialogConfig;
        this.currentCustomer = {} as Customer;
        this.currentCustomer.partnerid = this.guardService.getPartnerId();
        if (this.desktopTemplate) {
            target = {
                width: "400px",
                panelClass: "customModal",
                maxHeight: "100vh",
                disableClose: true,
                data: {
                    partnerdomain: this.partner.partnerdomain,
                    password: this.currentCustomer.password,
                    partnerid: this.currentCustomer.partnerid,
                    email: this.currentCustomer.email,
                    modeLogin: true,
                },
            };
        } else {
            target = {
                width: "100%",
                height: "100vh",
                maxWidth: "100%",
                panelClass: "customModal",
                disableClose: true,
                data: {
                    partnerdomain: this.partner.partnerdomain,
                    password: this.currentCustomer.password,
                    partnerid: this.currentCustomer.partnerid,
                    email: this.currentCustomer.email,
                    modeLogin: true,
                },
            };
        }
        const diagRef = this.dialog.open(PartnerloginComponent, target);
        diagRef
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => {
                if (result?.token?.length > 0) {
                    this.currentCustomer = result;
                    this.authService.setAuth(this.currentCustomer);
                }
            });
    }

    async logout() {
        // my account
        this.authService
            .logout(this.partner?.partnerid)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.offerpackService.resetOfferpackList();
                this.authService.resetCustomer(this.partner?.partnerid);
                const url = this.router.createUrlTree([""]).toString();
                this.router.navigateByUrl(url);
            });
    }

    async gotofavorites() {
        const url = this.router.createUrlTree(["favorites"]).toString();
        this.router.navigateByUrl(url);
    }

    async gotoOfferpack() {
        const url = this.router.createUrlTree(["offerpack"]).toString();
        this.router.navigateByUrl(url);
    }

    async gotohome() {
        // console.log('jala')
        const url = this.router.createUrlTree([""]).toString();
        // this.router.navigateByUrl(url);
        const link = document.createElement("a");
        link.href = url;
        link.click();
    }

    async gotoCalendar() {
        this.router.navigate(["terminkalender"]);
    }

    openChat() {
        this.chatService.setOpenChat();
    }

    async gotoContent() {
        const url = this.router.createUrlTree(["contents"]).toString();
        this.router.navigateByUrl(url);
    }

    async gotoAnfrage() {
        const url = this.router.createUrlTree(["anfrage"]).toString();
        this.router.navigateByUrl(url);
    }
}
